import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { LoginSchema, type LoginInput } from '@cutlyft/zod-schemas';
import { useLogin } from '@/hooks/auth/use-login';
import { useAuthStore } from '@/stores/auth.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';

const VITE_API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setAuth = useAuthStore((state) => state.setAuth);
  const loginMutation = useLogin();

  const form = useForm<LoginInput>({
    resolver: zodResolver(LoginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (data: LoginInput) => {
    loginMutation.mutate(data, {
      onSuccess: (res) => {
        setAuth(res.user, res.tenant, res.accessToken);
        // If there's a redirect query parameter, send the user there, otherwise dashboard
        const redirectUrl = searchParams.get('redirect');
        if (redirectUrl) {
          navigate(decodeURIComponent(redirectUrl));
        } else {
          navigate('/dashboard');
        }
      },
      onError: (err: any) => {
        const apiError = err.response?.data?.error;
        if (apiError?.code === 'INVALID_CREDENTIALS') {
          form.setError('email', { message: ' ' }); // clear message on email, detail on password
          form.setError('password', { message: 'Invalid email or password.' });
        } else if (apiError?.code === 'ACCOUNT_LOCKED') {
          form.setError('root', {
            message: 'Too many failed login attempts. Please try again later.',
          });
        } else if (apiError?.code === 'EMAIL_NOT_VERIFIED') {
          form.setError('root', {
            message: 'EMAIL_NOT_VERIFIED',
          });
        } else {
          form.setError('root', {
            message: apiError?.message || 'Failed to sign in. Please try again.',
          });
        }
      },
    });
  };

  const handleSsoGoogle = () => {
    window.location.href = `${VITE_API_URL}/auth/google`;
  };

  const handleSsoMicrosoft = () => {
    window.location.href = `${VITE_API_URL}/auth/microsoft`;
  };

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg-secondary p-6 relative overflow-hidden">
      {/* Decorative premium gradient background glow */}
      <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-[120px] pointer-events-none" />

      <div className="w-full max-w-md z-10 space-y-6">
        {/* Brand logo display */}
        <div className="text-center">
          <span className="text-3xl font-extrabold tracking-tight text-primary">Cutlyft</span>
          <p className="mt-2 text-sm text-fg-secondary">
            Precision 1D cutting stock optimisation for door and window fabricators
          </p>
        </div>

        <Card className="border border-border/50 shadow-xl bg-bg-elevated/80 backdrop-blur-md">
          <CardHeader>
            <CardTitle className="text-2xl text-fg-primary">Sign In</CardTitle>
            <CardDescription className="text-fg-secondary">
              Welcome back! Please enter your details to access your workspace.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                {form.formState.errors.root?.message === 'EMAIL_NOT_VERIFIED' ? (
                  <div className="p-3 text-sm rounded bg-warning-bg text-warning border border-warning/20 space-y-1">
                    <p className="font-semibold">Email not verified</p>
                    <p>Please click the verification link we sent to your inbox when you signed up. Check your spam folder if you can't find it.</p>
                  </div>
                ) : form.formState.errors.root ? (
                  <div className="p-3 text-sm rounded bg-error-bg text-error border border-error/20">
                    {form.formState.errors.root.message}
                  </div>
                ) : null}

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="john.doe@example.com" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex items-center justify-between">
                        <FormLabel>Password</FormLabel>
                        <Link
                          to="/forgot-password"
                          className="text-xs font-semibold text-primary hover:underline"
                        >
                          Forgot password?
                        </Link>
                      </div>
                      <FormControl>
                        <Input type="password" placeholder="••••••••" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="w-full mt-2 bg-primary hover:bg-primary/95 text-primary-foreground font-semibold h-11"
                  disabled={loginMutation.isPending}
                >
                  {loginMutation.isPending ? 'Signing In...' : 'Sign In'}
                </Button>
              </form>
            </Form>

            <div className="relative flex items-center justify-center my-4">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border/50" />
              </div>
              <span className="relative bg-bg-elevated px-3 text-xs uppercase text-fg-muted">
                Or continue with
              </span>
            </div>

            {/* SSO Action Buttons — Microsoft hidden until tenant provisioning is ready */}
            <div className="flex flex-col gap-3">
              <Button
                variant="outline"
                type="button"
                onClick={handleSsoGoogle}
                className="w-full border-border/50 hover:bg-secondary flex items-center justify-center gap-2 h-10 font-semibold"
              >
                <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z" fill="#EA4335"/>
                </svg>
                <span>Continue with Google</span>
              </Button>
            </div>
          </CardContent>
          <CardFooter className="flex justify-center border-t border-border/30 pt-6">
            <p className="text-sm text-fg-secondary">
              Don't have a workspace yet?{' '}
              <Link to="/signup" className="font-semibold text-primary hover:underline">
                Register Workspace
              </Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
